/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Represents a minimal Redux-like store.
 * @typeParam State - The shape of the store's state.
 * @param dispatch - Dispatches an action to update state.
 * @param select - Selects a slice of state using a selector function.
 * @param when - Registers a callback for one or more action types.
 * @param whenOnce - Registers a one-time callback for action types.
 * @param teardown - Cleans up any registered effects.
 */
export interface Store<State> {
  dispatch: (action: AnyAction) => void;
  read: <T>(selector: (state: State) => T) => T;
  select: <T>(
    selector: (state: State) => T,
    onChange: (value: T) => void,
  ) => () => void;
  when: <
    Actions extends readonly ActionCreator[],
    Handler extends (action: ActionType<Actions[number]>) => void,
  >(
    ...params: [...Actions, Handler]
  ) => () => void;
  whenOnce: <
    Actions extends readonly ActionCreator[],
    Handler extends (action: ActionType<Actions[number]>) => void,
  >(
    ...params: [...Actions, Handler]
  ) => () => void;
  createSignal: <Result>(
    selector: (state: State) => Result,
  ) => StateSignal<Result>;
  runEffects: () => () => void;
}

/**
 * ================================
 * ===        Scheduler         ===
 * ================================
 */
export interface Scheduler {
  scheduleTask(fn: () => void): number;
  cancelTask(id: number): void;
}

/**
 * Synchronous “trampoline” scheduler.
 *
 * All work executes in the same macrotask, but stack-safe:
 * tasks scheduled from inside other tasks are queued
 * and processed after the current one finishes.
 */
export class TrampolineScheduler implements Scheduler {
  private nextId = 0;
  private queue = new Map<number, () => void>();
  private active = false;

  private flush(): void {
    if (this.active) return;

    this.active = true;

    while (this.queue.size) {
      const entry = this.queue.entries().next().value;
      if (!entry) break;

      const [id, task] = entry;
      this.queue.delete(id);

      try {
        task();
      } catch (err) {
        // Surface errors asynchronously so one failure
        // doesn’t prevent later tasks from running
        setTimeout(() => {
          throw err;
        }, 0);
      }
    }

    this.active = false;
  }

  scheduleTask(fn: () => void): number {
    const id = ++this.nextId;
    this.queue.set(id, fn);

    this.flush();

    return id;
  }

  cancelTask(id: number): void {
    this.queue.delete(id);
  }
}

/**
 * ================================
 * ===         Actions          ===
 * ================================
 */

/**
 * A generic action object.
 *
 * @typedef AnyAction
 * @param type - The action type identifier.
 * @param payload - Optional payload for the action.
 */
type AnyAction = { type: string; payload: any } | { type: string };

/**
 * Defines a function type that produces payload objects for action creators.
 *
 * @typedef PropsFunction
 * @param payload - The payload for the action.
 * @returns The processed payload or void.
 */
type PropsFunction = (payload: any) => any | (() => void);

/**
 * Extracts the payload type from a PropsFunction.
 *
 * @typeParam T - A function type that defines action payload.
 * @typedef Payload
 */
type Payload<T extends PropsFunction> = T extends () => void
  ? void
  : T extends (payload: infer P) => infer P
    ? P
    : never;

/**
 * Factory type for creating strongly-typed action creator functions.
 *
 * @typeParam K - The action type string.
 * @typeParam T - A PropsFunction defining the payload shape.
 * @typedef ActionCreator
 */
type ActionCreator<
  K extends string = string,
  T extends PropsFunction = PropsFunction,
> = {
  type: K;
} & (Payload<T> extends void
  ? () => { type: K }
  : (payload: Payload<T>) => { type: K; payload: Payload<T> });

/**
 * Infers the action object type produced by an ActionCreator.
 *
 * @typeParam T - The ActionCreator type.
 * @typedef ActionType
 */
type ActionType<T> =
  T extends ActionCreator<infer K, infer R>
    ? R extends (payload: infer P) => infer P
      ? P extends void
        ? { type: K }
        : { type: K; payload: P }
      : never
    : never;

/**
 * A mapping of action name keys to their corresponding ActionCreator functions.
 *
 * @typeParam GroupName - The prefix group name for action types.
 * @typeParam T - An object of payload function definitions.
 * @typedef ActionCreators
 */
type ActionCreators<
  GroupName extends string,
  T extends { [key: string]: PropsFunction },
> = {
  [K in keyof T]: K extends string
    ? ActionCreator<`[${GroupName}] ${K}`, T[K]>
    : never;
};

/**
 * Creates a payload projector function that returns its argument.
 *
 * @typeParam T - The payload type.
 * @returns Function that returns the provided payload.
 */
export function props<T>(): (payload: T) => T {
  return (payload: T) => payload;
}

/**
 * Creates an action creator with no payload.
 *
 * @returns Function that produces an action with only a type.
 */
export function emptyProps(): () => void {
  return () => {};
}

/**
 * Generates a group of action creator functions with a common type prefix.
 *
 * @typeParam GroupName - The modifier for action types (e.g., feature name).
 * @typeParam T - An object whose values are payload creator functions.
 * @param name - The group prefix name.
 * @param group - An object mapping action names to payload functions.
 * @returns A set of action creators.
 */
export function createActionGroup<
  GroupName extends string,
  T extends { [key: string]: (...args: any[]) => any },
>(name: GroupName, group: T): ActionCreators<GroupName, T> {
  return Object.fromEntries(
    Object.entries(group).map(([key, value]) => [
      key,
      Object.assign(
        typeof value === 'function'
          ? (payload: Payload<typeof value>) => ({
              type: `[${name}] ${key}`,
              payload,
            })
          : () => ({ type: `[${name}] ${key}` }),
        { type: `[${name}] ${key}` },
      ),
    ]),
  ) as ActionCreators<GroupName, T>;
}

/**
 * ================================
 * ===         Reducers         ===
 * ================================
 */

/**
 * Creates a reducer function that responds to specified action types.
 *
 * @typeParam State - The type of the slice of state.
 * @typeParam Actions - An array of ActionCreator types to handle.
 * @param params - One or more action creators followed by a reducer handler.
 * @returns A reducer function.
 */
export function on<
  State,
  Actions extends readonly ActionCreator[],
  Handler extends (state: State, action: ActionType<Actions[number]>) => State,
>(
  ...params: [...Actions, Handler]
): (state: State, action: AnyAction) => State {
  const actionFns = params.slice(0, -1) as ActionCreator[];
  const reducerFn = params[params.length - 1] as (
    state: State,
    action: ActionType<Actions[number]>,
  ) => State;

  return (state: State, action: AnyAction) => {
    const shouldReduceState = actionFns.some(
      (param) => param.type === action.type,
    );
    if (!shouldReduceState) {
      return state;
    }
    return reducerFn(state, action as unknown as ActionType<Actions[number]>);
  };
}

/**
 * Combines multiple reducer functions into a single root reducer.
 *
 * @typeParam State - The combined state shape.
 * @param initialState - The initial state when undefined is passed.
 * @param reducers - One or more reducer functions.
 * @returns The root reducer.
 */
export function createReducer<State>(
  initialState: State,
  ...reducers: readonly ((state: State, action: { type: string }) => State)[]
) {
  return (state: State | undefined, action: { type: string }) => {
    return reducers.reduce(
      (acc, reducer) => reducer(acc, action),
      state === undefined ? initialState : state,
    );
  };
}

/**
 * ================================
 * ===         Effects          ===
 * ================================
 */

/**
 * Defines an effect that can subscribe to store actions and return a cleanup function.
 *
 * @param effectFn - Function that receives the store and returns a teardown callback.
 * @returns The provided effect function.
 */
type EffectFn = (store: Store<any>) => () => void;

/**
 * Creates an effect function that can subscribe to store actions and return a cleanup function.
 *
 * @param effectFn - Function that receives the store and returns a teardown callback.
 * @returns The provided effect function.
 */
export function createEffect(effectFn: EffectFn) {
  return effectFn;
}

/**
 * ================================
 * ===         Selectors          ===
 * ================================
 */

interface SelectConfig {
  debugName?: string;
}

/**
 * Creates a memoized selector from one or more input selectors and a projector function.
 * @param params - Input selector functions followed by a projector.
 * @returns A selector function that returns computed state.
 */
export function select<S, T0, R>(
  t0: (state: S) => T0,
  projectFn: (t0: T0) => R,
  config?: SelectConfig,
): (state: S) => R;
export function select<S, T0, T1, R>(
  t0: (state: S) => T0,
  t1: (state: S) => T1,
  projectFn: (t0: T0, t1: T1) => R,
  config?: SelectConfig,
): (state: S) => R;
export function select<S, T0, T1, T2, R>(
  t0: (state: S) => T0,
  t1: (state: S) => T1,
  t2: (state: S) => T2,
  projectFn: (t0: T0, t1: T1, t2: T2) => R,
  config?: SelectConfig,
): (state: S) => R;
export function select<S, T0, T1, T2, T3, R>(
  t0: (state: S) => T0,
  t1: (state: S) => T1,
  t2: (state: S) => T2,
  t3: (state: S) => T3,
  projectFn: (t0: T0, t1: T1, t2: T2, t3: T3) => R,
  config?: SelectConfig,
): (state: S) => R;
export function select<S, T0, T1, T2, T3, T4, R>(
  t0: (state: S) => T0,
  t1: (state: S) => T1,
  t2: (state: S) => T2,
  t3: (state: S) => T3,
  t4: (state: S) => T4,
  projectFn: (t0: T0, t1: T1, t2: T2, t3: T3, t4: T4) => R,
  config?: SelectConfig,
): (state: S) => R;
export function select<S, T0, T1, T2, T3, T4, T5, R>(
  t0: (state: S) => T0,
  t1: (state: S) => T1,
  t2: (state: S) => T2,
  t3: (state: S) => T3,
  t4: (state: S) => T4,
  t5: (state: S) => T5,
  projectFn: (t0: T0, t1: T1, t2: T2, t3: T3, t4: T4, t5: T5) => R,
  config?: SelectConfig,
): (state: S) => R;
export function select<S, T0, T1, T2, T3, T4, T5, T6, R>(
  t0: (state: S) => T0,
  t1: (state: S) => T1,
  t2: (state: S) => T2,
  t3: (state: S) => T3,
  t4: (state: S) => T4,
  t5: (state: S) => T5,
  t6: (state: S) => T6,
  projectFn: (t0: T0, t1: T1, t2: T2, t3: T3, t4: T4, t5: T5, t6: T6) => R,
  config?: SelectConfig,
): (state: S) => R;
export function select(...params: any[]): (state: any) => any {
  let inputs = params.slice(0, -1) as unknown as ((state: any) => any)[];
  let selectFn = params[params.length - 1] as (...args: any[]) => any;
  let config: SelectConfig | undefined;

  if (typeof selectFn !== 'function') {
    config = selectFn;
    selectFn = params[params.length - 2];
    inputs = params.slice(0, -2) as unknown as ((state: any) => any)[];
  }

  let lastInputValues: any[] = [];
  let lastOutput: any;

  return (state: any) => {
    const inputValues = inputs.map((input) => input(state)) as any[];
    if (
      inputValues.some((value, index) => {
        const isMismatched = value !== lastInputValues[index];

        if (isMismatched && config && config.debugName) {
          console.log(
            'Select Argument Mismatch:',
            config.debugName,
            `input[${index}]`,
            'last:',
            lastInputValues[index],
            'now:',
            value,
          );
        }

        return isMismatched;
      })
    ) {
      lastInputValues = inputValues;
      lastOutput = selectFn(...inputValues);
    }
    return lastOutput;
  };
}

/**
 * ================================
 * ===    State Observable      ===
 * ================================
 */
/**
 * @public
 */
export interface StateSignal<State> {
  (): State;
  subscribe(onChange: (value: State) => void): () => void;
}

/**
 * ================================
 * ===         Store            ===
 * ================================
 */

/**
 * Creates a store with reducers and effects.
 * @typeParam Reducers - An object mapping keys to reducer functions.
 * @typeParam State - The resulting state shape inferred from Reducers.
 * @param config - Configuration object.
 * @returns The initialized store instance.
 */
export function createStore<
  Reducers extends {
    [key: string]: (state: any | undefined, action: AnyAction) => unknown;
  },
  State extends {
    [K in keyof Reducers]: Reducers[K] extends (
      state: infer R | undefined,
      action: any,
    ) => infer R
      ? R
      : never;
  },
>(config: {
  debugName?: string;
  reducers: Reducers;
  effects: EffectFn[];
  projectStateForDevtools?: (state: State) => object;
}): Store<State> {
  const scheduler = new TrampolineScheduler();
  const devtools = config.debugName
    ? connectToChromeExtension({ name: config.debugName })
    : undefined;
  const reducerFnEntries = Object.entries(config.reducers);
  const reducerFn = function (
    state: State | undefined,
    action: AnyAction,
  ): State {
    return reducerFnEntries.reduce((acc, [key, value]) => {
      return { ...acc, [key]: value(acc?.[key], action) } as State;
    }, state) as State;
  };
  const whenCallbackFnMap = new Map<
    string,
    Array<(action: AnyAction) => void>
  >();
  const selectCallbackFns: Array<() => void> = [];

  let state = reducerFn(undefined, { type: '@@init' });

  function dispatch(action: AnyAction) {
    scheduler.scheduleTask(() => {
      state = reducerFn(state, action);

      const whenCallbackFns = whenCallbackFnMap.get(action.type) ?? [];
      whenCallbackFns.forEach((callback) => callback(action));
      selectCallbackFns.forEach((callback) => callback());

      devtools?.send(action, config.projectStateForDevtools?.(state) ?? state);
    });
  }

  function when<
    Actions extends ActionCreator<any, any>[],
    Params extends [...Actions, (action: AnyAction) => void],
  >(...params: Params): () => void {
    const actionFns = params.slice(0, -1) as ActionCreator<any, any>[];
    const callbackFn = params[params.length - 1] as (action: AnyAction) => void;

    actionFns.forEach((actionFn) => {
      if (!whenCallbackFnMap.has(actionFn.type)) {
        whenCallbackFnMap.set(actionFn.type, []);
      }
      whenCallbackFnMap.get(actionFn.type)?.push(callbackFn);
    });

    return () => {
      actionFns.forEach((actionFn) => {
        const callbacks = whenCallbackFnMap.get(actionFn.type) ?? [];

        whenCallbackFnMap.set(
          actionFn.type,
          callbacks.filter((cb) => cb !== callbackFn),
        );
      });
    };
  }

  function whenOnce<
    Actions extends ActionCreator<any, any>[],
    Params extends [...Actions, (action: AnyAction) => void],
  >(...params: Params): () => void {
    const actionFns = params.slice(0, -1) as ActionCreator<any, any>[];
    const callbackFn = params[params.length - 1] as (action: AnyAction) => void;

    const cleanupFn = when(...actionFns, (action: AnyAction) => {
      callbackFn(action);
      cleanupFn();
    });

    return cleanupFn;
  }

  function read<T>(selector: (state: State) => T): T {
    return selector(state);
  }

  function select<T>(
    selector: (state: State) => T,
    onChange: (value: T) => void,
  ) {
    let currentValue = read(selector);

    onChange(currentValue);

    const callback = () => {
      const newValue = read(selector);
      if (newValue !== currentValue) {
        currentValue = newValue;
        onChange(newValue);
      }
    };

    selectCallbackFns.push(callback);

    return () => {
      selectCallbackFns.splice(selectCallbackFns.indexOf(callback), 1);
    };
  }

  function createSignal<Result>(
    selector: (state: State) => Result,
  ): StateSignal<Result> {
    return Object.assign(() => read(selector), {
      subscribe: (onChange: (value: Result) => void) =>
        select(selector, onChange),
    });
  }

  function runEffects() {
    devtools?.init(config.projectStateForDevtools?.(state) ?? state);

    const cleanupFns = config.effects.map((effect) => effect(store));

    return () => {
      cleanupFns.forEach((fn) => fn());
      devtools?.unsubscribe();
    };
  }

  const store: Store<State> = {
    dispatch,
    read,
    select,
    when: when as Store<State>['when'],
    whenOnce: whenOnce as Store<State>['whenOnce'],
    createSignal,
    runEffects,
  };

  return store;
}

/**
 * ================================
 * ===         Entities         ===
 * ================================
 */

/**
 * Maintains a normalized collection of entities.
 * @typeParam Entity - The type of the entity.
 * @param ids - Array of entity IDs.
 * @param entities - Map of IDs to entity objects.
 */
export interface EntityState<Entity> {
  ids: string[];
  entities: Record<string, Entity>;
}

/**
 * Describes a partial update to a single entity.
 * @typeParam Entity - The entity type.
 * @param id - The target entity ID.
 * @param updates - The fields to update.
 */
export interface EntityChange<Entity> {
  id: string;
  updates: Partial<Entity>;
}

/**
 * Provides methods to manage a collection of entities.
 * @typeParam Entity - The entity type.
 * @param updateOne - Updates a single entity.
 * @param updateMany - Updates multiple entities.
 * @param addOne - Adds a single new entity.
 * @param addMany - Adds multiple new entities.
 * @param removeOne - Removes a single entity by ID.
 * @param removeMany - Removes multiple entities by ID.
 */
export interface EntityAdapter<Entity> {
  updateOne: (
    state: EntityState<Entity>,
    changes: EntityChange<Entity>,
  ) => EntityState<Entity>;
  updateMany: (
    state: EntityState<Entity>,
    changes: EntityChange<Entity>[],
  ) => EntityState<Entity>;
  addOne: (state: EntityState<Entity>, entity: Entity) => EntityState<Entity>;
  addMany: (
    state: EntityState<Entity>,
    entities: Entity[],
  ) => EntityState<Entity>;
  removeOne: (state: EntityState<Entity>, id: string) => EntityState<Entity>;
  removeMany: (
    state: EntityState<Entity>,
    ids: string[],
  ) => EntityState<Entity>;
}

/**
 * Creates an EntityAdapter for performing immutable updates on entity collections.
 * @typeParam Entity - The entity type.
 * @param config - Configuration with a selectId function.
 * @returns Adapter with CRUD methods for entity state.
 */
export function createEntityAdapter<Entity>(config: {
  selectId: (entity: Entity) => string;
}): EntityAdapter<Entity> {
  const { selectId } = config;

  function updateOne(
    state: EntityState<Entity>,
    changes: { id: string; updates: Partial<Entity> },
  ) {
    return {
      ...state,
      entities: {
        ...state.entities,
        [changes.id]: { ...state.entities[changes.id], ...changes.updates },
      },
    };
  }

  function updateMany(
    state: EntityState<Entity>,
    changes: { id: string; updates: Partial<Entity> }[],
  ) {
    return changes.reduce((acc, change) => updateOne(acc, change), state);
  }

  function addOne(state: EntityState<Entity>, entity: Entity) {
    return {
      ...state,
      ids: [...state.ids, selectId(entity)],
      entities: { ...state.entities, [selectId(entity)]: entity },
    };
  }

  function addMany(state: EntityState<Entity>, entities: Entity[]) {
    return entities.reduce((acc, entity) => addOne(acc, entity), state);
  }

  function removeOne(
    state: EntityState<Entity>,
    idToRemove: string,
  ): EntityState<Entity> {
    const updatedEntities = { ...state.entities };

    delete updatedEntities[idToRemove];

    return {
      ...state,
      ids: state.ids.filter((id) => id !== idToRemove),
      entities: updatedEntities,
    };
  }

  function removeMany(state: EntityState<Entity>, ids: string[]) {
    return {
      ...state,
      ids: state.ids.filter((id) => !ids.includes(id)),
      entities: Object.fromEntries(
        Object.entries(state.entities).filter(([id]) => !ids.includes(id)),
      ),
    };
  }

  return { updateOne, updateMany, addOne, addMany, removeOne, removeMany };
}

/**
 * ================================
 * ===         Devtools         ===
 * ================================
 */

export interface DevtoolsChromeExtension {
  connect: (options: { name: string }) => DevtoolsChromeExtensionConnection;
}

export interface DevtoolsChromeExtensionConnection {
  unsubscribe: () => void;
  send: (action: AnyAction, state: object) => void;
  init: (state: object) => void;
  error: (message: string) => void;
}

export function connectToChromeExtension(options: {
  name: string;
}): DevtoolsChromeExtensionConnection | undefined {
  if (typeof window === 'undefined') {
    return;
  }

  const extension = (window as any).__REDUX_DEVTOOLS_EXTENSION__ as
    | DevtoolsChromeExtension
    | undefined;

  if (!extension) {
    return;
  }

  return extension.connect({
    name: options.name,
  });
}
